import type { TokenSet } from "../emit/layers.ts";

/**
 * The primitive layer of `contracts/design-system.md`: the twelve-step neutral ramp per
 * theme, the eight accents, the eleven spacing steps, the ten type steps, the five radii,
 * the five elevations, the four durations, the three easings and the five breakpoints.
 * Named by what they are, never by where they are used. Every hex value is the document's.
 */

/** § Neutral ramp, light column, steps 1 to 12. */
export const neutralLight = [
  "#fcfcfd", "#f7f8f9", "#f0f1f3", "#e8eaec", "#dfe2e6", "#d6d9de",
  "#caced4", "#b5bbc3", "#8e939a", "#7b828a", "#666f7a", "#23272b",
] as const;

/** § Neutral ramp, dark column, steps 1 to 12: pure greys, R = G = B (2026-09-28, by the owner's
 * decision), each the grey whose relative luminance is nearest the step it replaced, so the ramp's
 * order and every ratio on it hold. Step 11, the text step, is the lowest grey that reaches 4.5:1
 * on step 5, the hover fill of the dark sheet, since the sheet moved to step 4 (2026-09-28, the
 * owner's QA session: a lighter outer card). */
export const neutralDark = [
  "#111111", "#181818", "#202020", "#272727", "#2e2e2e", "#373737",
  "#464646", "#515151", "#606060", "#727272", "#959595", "#ededed",
] as const;

export const accentNames = ["blue", "green", "amber", "red", "purple", "teal", "pink", "orange"] as const;
export type AccentName = (typeof accentNames)[number];

/** § Accent ramps: the tinted background (step 3), the solid (step 9) with its foreground,
 * and the text colour (step 11), per theme where the document gives one per theme. The dark text
 * step is the 2026-09-25 value tinted toward white until it reaches 4.5:1 on dark neutral 5, the
 * hover fill of the dark sheet (2026-09-28, with the sheet at neutral 4). */
export const accents: Readonly<
  Record<AccentName, { bgLight: string; bgDark: string; solid: string; onSolid: string; textLight: string; textDark: string }>
> = {
  blue: { bgLight: "#e3f2ff", bgDark: "#122134", solid: "#3077cd", onSolid: "#ffffff", textLight: "#346eb5", textDark: "#6b98d0" },
  green: { bgLight: "#e3f6e6", bgDark: "#112616", solid: "#1b8842", onSolid: "#ffffff", textLight: "#2a7d43", textDark: "#65a374" },
  amber: { bgLight: "#fdeeda", bgDark: "#2d1d05", solid: "#ffce56", onSolid: "#23272b", textLight: "#9b6000", textDark: "#ba8d40" },
  red: { bgLight: "#ffe9e6", bgDark: "#321816", solid: "#c5514d", onSolid: "#ffffff", textLight: "#af4e49", textDark: "#cd807b" },
  purple: { bgLight: "#f3ecff", bgDark: "#231c31", solid: "#8964c2", onSolid: "#ffffff", textLight: "#7c5eac", textDark: "#a08ac8" },
  teal: { bgLight: "#ddf6f4", bgDark: "#052624", solid: "#00857f", onSolid: "#ffffff", textLight: "#007b76", textDark: "#35a49f" },
  pink: { bgLight: "#ffe9f3", bgDark: "#2f1823", solid: "#b85488", onSolid: "#ffffff", textLight: "#a4517b", textDark: "#c381a1" },
  orange: { bgLight: "#ffebde", bgDark: "#311a0c", solid: "#ffbd7a", onSolid: "#23272b", textLight: "#ab5417", textDark: "#ca845b" },
};

/** § Focus ring. */
export const focusRing = { light: "#347bd1", dark: "#4b93eb" } as const;

/** § Semantic tokens, the two scrims. */
export const scrim = { light: "rgb(35 39 43 / 0.44)", dark: "rgb(0 0 0 / 0.62)" } as const;

/** § Semantic tokens, the shade at the end of a list's pinned start while its columns scroll under it (2026-10-04), in the elevation shadows' tone. */
export const edgeShade = { light: "rgb(35 39 43 / 0.12)", dark: "rgb(0 0 0 / 0.45)" } as const;

/** § Semantic tokens, the solid-fill hover and press overlays (2026-09-14: aligned to Spectrum UI by the owner's UI review). */
export const solidOverlay = { hover: "rgb(0 0 0 / 0.10)", active: "rgb(0 0 0 / 0.20)" } as const;

/** § Spacing, `--space-0` to `--space-10`. */
export const space = ["0", "2px", "4px", "8px", "12px", "16px", "24px", "32px", "48px", "64px", "96px"] as const;

/** § Type: size and line height in px, weight, letter spacing. */
export const type = {
  display: { size: 30, line: 36, weight: 600, tracking: "-0.02em" },
  h1: { size: 24, line: 32, weight: 600, tracking: "-0.015em" },
  h2: { size: 20, line: 28, weight: 600, tracking: "-0.01em" },
  h3: { size: 16, line: 24, weight: 600, tracking: "0" },
  body: { size: 14, line: 20, weight: 400, tracking: "0" },
  "body-strong": { size: 14, line: 20, weight: 550, tracking: "0" },
  small: { size: 13, line: 18, weight: 400, tracking: "0" },
  caption: { size: 12, line: 16, weight: 400, tracking: "0.005em" },
  micro: { size: 11, line: 14, weight: 550, tracking: "0.02em" },
  mono: { size: 13, line: 20, weight: 450, tracking: "0" },
} as const;

export const fonts = {
  sans: 'ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif',
  mono: 'ui-monospace, "SF Mono", "Cascadia Mono", Menlo, Consolas, monospace',
} as const;

/** § Radii. */
export const radius = { xs: "4px", sm: "6px", md: "8px", lg: "12px", full: "9999px" } as const;

/** § Elevation, per theme. The board card's pair (2026-09-29, by the owner's review: "the dropshadow has to be darker") is its own: the hairline elevation 1 lifted a white card off the light column by nothing a reader could see, and elevation 1 also shades every input, checkbox and switch, which keep it. */
export const shadow = {
  light: {
    "1": "0 1px 2px rgb(35 39 43 / 0.06)",
    "2": "0 4px 12px rgb(35 39 43 / 0.10)",
    "3": "0 12px 32px rgb(35 39 43 / 0.14)",
    drag: "0 8px 20px rgb(35 39 43 / 0.18)",
    card: "0 1px 3px rgb(35 39 43 / 0.16), 0 2px 6px rgb(35 39 43 / 0.08)",
    "card-hover": "0 1px 3px rgb(35 39 43 / 0.16), 0 6px 16px rgb(35 39 43 / 0.14)",
  },
  dark: {
    "1": "0 1px 2px rgb(0 0 0 / 0.40)",
    "2": "0 4px 12px rgb(0 0 0 / 0.50)",
    "3": "0 12px 32px rgb(0 0 0 / 0.60)",
    drag: "0 8px 20px rgb(0 0 0 / 0.65)",
    card: "0 1px 3px rgb(0 0 0 / 0.50)",
    "card-hover": "0 1px 3px rgb(0 0 0 / 0.50), 0 6px 16px rgb(0 0 0 / 0.45)",
  },
} as const;

/** § Motion: exactly four durations and three easings (FR-015-103, W-19). */
export const duration = { instant: "0ms", fast: "100ms", base: "150ms", slow: "250ms" } as const;
export const easing = {
  out: "cubic-bezier(0.16, 1, 0.3, 1)",
  "in-out": "cubic-bezier(0.4, 0, 0.2, 1)",
  linear: "linear",
} as const;

/** § Breakpoints. */
export const breakpoint = { sm: "640px", md: "768px", lg: "1024px", xl: "1280px", "2xl": "1536px" } as const;

const rem = (px: number) => `${px / 16}rem`;

function build(): TokenSet {
  const out: Record<string, string> = {};
  neutralLight.forEach((hex, i) => (out[`neutral-light-${i + 1}`] = hex));
  neutralDark.forEach((hex, i) => (out[`neutral-dark-${i + 1}`] = hex));
  for (const name of accentNames) {
    const a = accents[name];
    out[`${name}-3-light`] = a.bgLight;
    out[`${name}-3-dark`] = a.bgDark;
    out[`${name}-9`] = a.solid;
    out[`${name}-9-on`] = a.onSolid;
    out[`${name}-11-light`] = a.textLight;
    out[`${name}-11-dark`] = a.textDark;
  }
  out["focus-light"] = focusRing.light;
  out["focus-dark"] = focusRing.dark;
  out["scrim-light"] = scrim.light;
  out["scrim-dark"] = scrim.dark;
  out["edge-shade-light"] = edgeShade.light;
  out["edge-shade-dark"] = edgeShade.dark;
  out["solid-hover-overlay"] = solidOverlay.hover;
  out["solid-active-overlay"] = solidOverlay.active;
  out.white = "#ffffff";
  space.forEach((value, i) => (out[`space-${i}`] = value));
  for (const [name, step] of Object.entries(type)) {
    out[`text-${name}-size`] = rem(step.size);
    out[`text-${name}-line`] = rem(step.line);
    out[`text-${name}-weight`] = String(step.weight);
    out[`text-${name}-tracking`] = step.tracking;
  }
  out["font-sans"] = fonts.sans;
  out["font-mono"] = fonts.mono;
  for (const [name, value] of Object.entries(radius)) out[`radius-${name}`] = value;
  for (const [theme, set] of Object.entries(shadow)) {
    for (const [name, value] of Object.entries(set)) out[`shadow-${name}-${theme}`] = value;
  }
  for (const [name, value] of Object.entries(duration)) out[`duration-${name}`] = value;
  for (const [name, value] of Object.entries(easing)) out[`ease-${name}`] = value;
  for (const [name, value] of Object.entries(breakpoint)) out[`bp-${name}`] = value;
  return out;
}

export const primitives: TokenSet = build();

/** The names of the duration tokens, which the reduced-motion override zeroes. */
export const durationTokens = Object.keys(duration).map((name) => `duration-${name}`);
